import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AddonCatalogEntry } from '~/api/models';
import { AddonsService } from '~/api/services/admin';
import { Logger } from '~/core';
import type { Verdict } from '~/plugins/entitlement';

export interface PaywallTarget {
    feature: string;
    requiresPlan?: string;
    requiresAddon?: string;
    /** Route name whose `navigation.*.title` names the thing the admin tried to reach. */
    routeName?: string;
}

/**
 * Backs all three paywall surfaces -- the sidebar modal, the locked tab, and the 402
 * page -- so they cannot drift apart.
 *
 * The addon catalog is fetched lazily and only once. Lock state itself never needs it:
 * that rides along in the feature payload the app already holds. The catalog only
 * supplies the commercial copy for the modal, and it costs a billing round-trip, so it
 * is not worth paying for on every page load.
 */
export const usePaywallStore = defineStore('paywall', () => {
    const target = ref<PaywallTarget | null>(null);
    const catalog = ref<AddonCatalogEntry[]>([]);
    const loading = ref(false);

    let inFlight: Promise<void> | null = null;

    const loadCatalog = (): Promise<void> => {
        if (catalog.value.length > 0) return Promise.resolve();
        if (inFlight) return inFlight;

        loading.value = true;
        inFlight = AddonsService.list()
            .then((addons) => {
                catalog.value = addons;
            })
            .catch((error) => {
                // Billing being unreachable must not break the paywall: the modal falls
                // back to the plan requirement, which comes from the feature payload.
                Logger.warn('Paywall', 'Could not load the addon catalog', error);
            })
            .finally(() => {
                loading.value = false;
                inFlight = null;
            });

        return inFlight;
    };

    /** The catalog entry backing the current target, if there is one. */
    const addon = computed(() => {
        const name = target.value?.requiresAddon;
        if (!name) return undefined;

        return catalog.value.find((entry) => entry.name === name);
    });

    const open = (next: PaywallTarget): void => {
        target.value = next;
        void loadCatalog();
    };

    /** Open from a route gate verdict. Anything but `locked` is not for sale. */
    const openFor = (verdict: Verdict, routeName?: string): boolean => {
        if (verdict.state !== 'locked') return false;

        open({
            feature: verdict.feature,
            requiresPlan: verdict.requiresPlan,
            requiresAddon: verdict.requiresAddon,
            routeName,
        });

        return true;
    };

    const close = (): void => {
        target.value = null;
    };

    return {
        target: computed(() => target.value),
        catalog: computed(() => catalog.value),
        addon,
        loading: computed(() => loading.value),
        isOpen: computed(() => target.value !== null),
        open,
        openFor,
        close,
        loadCatalog,
    };
});
